import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { FileText, Upload, Loader2, ArrowLeft, Sparkles, Wand2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function UploadZone({ onFileSelect, uploading }) {
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) onFileSelect(file);
  }, [onFileSelect]);

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className="border-2 border-dashed border-slate-300 dark:border-slate-600 p-12 text-center hover:border-[#FB923C] transition-colors cursor-pointer"
      data-testid="upload-zone"
      onClick={() => document.getElementById('resume-file-input')?.click()}
    >
      <input
        id="resume-file-input"
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) onFileSelect(e.target.files[0]); }}
        data-testid="file-input"
      />
      {uploading ? (
        <Loader2 className="w-12 h-12 animate-spin text-[#FB923C] mx-auto mb-4" />
      ) : (
        <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
      )}
      <p className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
        {uploading ? 'Extracting with AI...' : 'Drop your resume here'}
      </p>
      <p className="text-sm text-slate-500 dark:text-slate-400">PDF, DOCX, or TXT (max 10MB)</p>
    </div>
  );
}

function RegeneratePanel({ extractedData, onRegenerate, regenerating }) {
  const [mode, setMode] = useState('professional');
  const [jobRole, setJobRole] = useState('');

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 space-y-4" data-testid="regenerate-panel">
      <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
        <Wand2 className="w-5 h-5 inline mr-2 text-[#FB923C]" />
        AI Resume Enhancement
      </h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Enhancement Mode</p>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="regen-mode-select" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="professional">Make it Professional</SelectItem>
              <SelectItem value="creative">Make it Creative</SelectItem>
              <SelectItem value="ats_optimized">Optimize for ATS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {mode === 'ats_optimized' && (
          <div>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Target Job Role</p>
            <Textarea
              data-testid="job-role-input"
              value={jobRole}
              onChange={(e) => setJobRole(e.target.value)}
              placeholder="e.g. Senior Software Engineer"
              rows={1}
              className="rounded-none"
            />
          </div>
        )}
      </div>
      <Button
        data-testid="regenerate-btn"
        onClick={() => onRegenerate(mode, jobRole)}
        disabled={regenerating}
        className="w-full bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-6 py-3 transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A]"
      >
        {regenerating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
        {regenerating ? 'Enhancing...' : 'Enhance with AI'}
      </Button>
    </div>
  );
}

export default function ResumeUploadPage() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  const handleFileSelect = useCallback(async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/resumes/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExtractedData(data.extracted_data);
      toast.success('Resume extracted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract resume');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleRegenerate = useCallback(async (mode, jobRole) => {
    if (!extractedData) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/ai/regenerate`, {
        resume_data: extractedData,
        mode,
        job_role: jobRole || undefined
      }, { withCredentials: true });
      setExtractedData(data.improved_data);
      toast.success(`Resume enhanced in ${mode.replace('_', ' ')} mode!`);
    } catch (error) {
      toast.error('Failed to enhance resume');
    } finally {
      setRegenerating(false);
    }
  }, [extractedData]);

  const handleUseData = useCallback(async () => {
    if (!extractedData) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/resumes`, {
        title: extractedData.personal_info?.name ? `${extractedData.personal_info.name}'s Resume` : 'Uploaded Resume',
        template: 'modern',
        data: extractedData
      }, { withCredentials: true });
      navigate(`/builder/${data.id}`);
      toast.success('Resume created! Edit it in the builder.');
    } catch (error) {
      toast.error('Failed to create resume');
    }
  }, [extractedData, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-[#FB923C]" strokeWidth={2} />
            <span className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
          </div>
          <Button data-testid="back-dashboard-btn" onClick={() => navigate('/dashboard')} className="bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 rounded-none px-4 py-2 text-sm">
            <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="upload-heading">
            Upload Your Resume
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            AI will extract your data and let you enhance it instantly
          </p>
        </div>

        <div className="space-y-6">
          <UploadZone onFileSelect={handleFileSelect} uploading={uploading} />

          {extractedData && (
            <>
              <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6" data-testid="extracted-preview">
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Extracted Data</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-slate-700 dark:text-slate-300">Name: <span className="font-normal">{extractedData.personal_info?.name || '—'}</span></p>
                    <p className="font-semibold text-slate-700 dark:text-slate-300">Email: <span className="font-normal">{extractedData.personal_info?.email || '—'}</span></p>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-700 dark:text-slate-300">Experience: <span className="font-normal">{extractedData.work_experience?.length || 0} positions</span></p>
                    <p className="font-semibold text-slate-700 dark:text-slate-300">Education: <span className="font-normal">{extractedData.education?.length || 0} entries</span></p>
                  </div>
                </div>
                {extractedData.professional_summary && (
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-3">{extractedData.professional_summary}</p>
                )}
              </div>

              <RegeneratePanel extractedData={extractedData} onRegenerate={handleRegenerate} regenerating={regenerating} />

              <Button
                data-testid="use-extracted-btn"
                onClick={handleUseData}
                className="w-full bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-8 py-4 text-lg transition-all hover:-translate-y-1 shadow-[4px_4px_0px_#0F172A] hover:shadow-[6px_6px_0px_#0F172A]"
              >
                Use This Data in Builder
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
